import { useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import { CuffaAPI } from 'api/cuffa/cuffa.api';
import { buildLandCategoryPayload, parseSavedLandCategory } from './helpers';
import categoriaFundiariaValidationSchema from './validation';
import { ROUTES } from 'routes/constants';

// payload builder reused from helpers

// parser reused from helpers

export function useCategoriaFundiaria() {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();

  const advanceNext = useRef(false);
  const shouldNavigate = useRef(false);

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarError, setSnackbarError] = useState(false);

  const initialValues = {
    landCategory: '',
    codigoAssentamento: '',
    nomePncFTerraBrasilBancoDaTerra: '',
    nomeComunidadeQuilombola: '',
    nomeComunidadeTradicional: '',
    nomeTerraIndigena: '',
  };

  const formik = useFormik({
    initialValues,
    validationSchema: categoriaFundiariaValidationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (!uuid) {
        setSnackbarMessage("Não é possível salvar sem um UUID de coleta válido.");
        setSnackbarError(true);
        setOpenSnackbar(true);
        return;
      }

      setSaving(true);
      shouldNavigate.current = false;

      const payload = buildLandCategoryPayload(values);

      try {
        console.log('useCategoriaFundiaria onSubmit', { uuid, payload });
        await CuffaAPI.updateLandCategory(uuid, payload);
        setSnackbarMessage('Dados salvos com sucesso!');
        setSnackbarError(false);
        setOpenSnackbar(true);
        shouldNavigate.current = true;

        // Salva seleção de categoria fundiária no localStorage em 'cuffa-storage'
        try {
          const storageKey = 'cuffa-storage';
          const existingRaw = typeof window !== 'undefined' ? localStorage.getItem(storageKey) : null;
          const existing = existingRaw ? JSON.parse(existingRaw) : {};
          const existingDados = existing?.state?.dadosParciais || {};
          const categoriaPayload = {
            landCategory: values.landCategory,
            codigoAssentamento: values.codigoAssentamento || '',
            nomePncFTerraBrasilBancoDaTerra: values.nomePncFTerraBrasilBancoDaTerra || '',
            nomeComunidadeQuilombola: values.nomeComunidadeQuilombola || '',
            nomeComunidadeTradicional: values.nomeComunidadeTradicional || '',
            nomeTerraIndigena: values.nomeTerraIndigena || '',
          };
          const newDadosParciais = { ...existingDados, categoriaFundiaria: categoriaPayload };
          const toStore = { ...existing, state: { ...existing.state, dadosParciais: newDadosParciais } };
          if (typeof window !== 'undefined') {
            localStorage.setItem(storageKey, JSON.stringify(toStore));
          }
        } catch (e) {
          console.warn('Falha ao salvar categoria fundiaria no localStorage', e);
        }
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || err.message || 'Erro ao salvar os dados.';
        setSnackbarMessage(errorMessage);
        setSnackbarError(true);
        setOpenSnackbar(true);
      } finally {
        setSaving(false);
      }
    },
  });

  useEffect(() => {
    if (!uuid) {
      setSnackbarMessage('UUID da coleta não encontrado na URL.');
      setSnackbarError(true);
      setOpenSnackbar(true);
      setLoading(false);
    }
  }, [uuid]);

  useEffect(() => {
    const fetchExisting = async () => {
      if (!uuid) {
        setLoading(false);
        return;
      }

      try {
        // Primeiro tenta carregar do localStorage 'cuffa-storage'
        const storageKey = 'cuffa-storage';
        const raw = typeof window !== 'undefined' ? localStorage.getItem(storageKey) : null;
        if (raw) {
          try {
            const parsed = JSON.parse(raw);
            const saved = parsed?.state?.dadosParciais?.categoriaFundiaria;
            if (saved) {
              const formData = {
                landCategory: saved.landCategory || '',
                codigoAssentamento: saved.codigoAssentamento || '',
                nomePncFTerraBrasilBancoDaTerra: saved.nomePncFTerraBrasilBancoDaTerra || '',
                nomeComunidadeQuilombola: saved.nomeComunidadeQuilombola || '',
                nomeComunidadeTradicional: saved.nomeComunidadeTradicional || '',
                nomeTerraIndigena: saved.nomeTerraIndigena || '',
              };
              formik.setValues(formData);
              setLoading(false);
              return;
            }
          } catch (e) {
            console.warn('Falha ao parsear cuffa-storage ao restaurar categoria fundiaria', e);
          }
        }

        // Se não encontrou no storage, busca na API
        const response = await CuffaAPI.getDataByID(uuid);
        if (response?.dados) {
          const data = response.dados;
          const formData = parseSavedLandCategory(data);
          formik.setValues(formData);
        }
      } catch (error) {
        console.error('Erro ao carregar dados existentes:', error);
        setSnackbarMessage('Erro ao carregar dados existentes.');
        setSnackbarError(true);
        setOpenSnackbar(true);
      } finally {
        setLoading(false);
      }
    };

    fetchExisting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid]);

  const handleSubmit = async (advance: boolean) => {
    advanceNext.current = advance;

    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      formik.setTouched(errors as any);
      return;
    }

    await formik.submitForm();
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);

    if (shouldNavigate.current && uuid && advanceNext.current) {
      const nextRoute = ROUTES.PRODUCAO_AGROECOLOGICA_INCLUIR.replace(':uuid', uuid);
      navigate(nextRoute);
    }

    shouldNavigate.current = false;
  };

  return {
    formik,
    loading,
    saving,
    openSnackbar,
    snackbarMessage,
    snackbarError,
    handleCloseSnackbar,
    handleSubmit,
    advanceNext,
  } as const;
}

export default useCategoriaFundiaria;

// Backwards-compatible named exports used by tests and other modules
export const buildPayloadFromValues = buildLandCategoryPayload;
export const parseSavedData = parseSavedLandCategory;
